import React, { useState, useEffect, useCallback } from 'react';
import ProjectService from '../../../services/ProjectService';
import Tabs from '../Tabs';
import LoadingSpinner from '../LoadingSpinner';
import PhiusProjectDataTab from './PhiusProjectDataTab';
import ProjectOverviewTab from './ProjectOverviewTab';
import ProjectInformationTab from './ProjectInformationTab';
import WallFloorRoofTab from './WallFloorRoofTab';
import WindowDoorTab from './WindowDoorTab';
import VentilationHeatingCoolingTab from './VentilationHeatingCoolingTab';
import OtherEquipmentTab from './OtherEquipmentTab';
import PhotosTab from './PhotosTab';
import './ProjectDetail.css';

// All form state stays lifted in this shell: handleSubmit's {...project} spread is the only
// thing that reaches Save, so a tab component owning its own project/editable state would
// silently drop its edits from every Save with no visible symptom. Tabs get state via props.
export default function ProjectDetail(props) {
  const [project, setProject] = useState(props?.project);
  const [loading, setLoading] = useState(true);
  const [editable, toggleEdit] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [invalidFields, setInvalidFields] = useState(new Set());

  useEffect(() => {
    setLoading(false);
    ProjectService.getAllPolicies()
      .then((policies) => {
        setPolicies(policies?.policies ?? []);
      })
      .catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProject({
      ...project,
      [name]: type === 'checkbox' ? checked : value,
    });
  };
  const handleFieldValidityChange = useCallback((name, isValid) => {
    setInvalidFields((prev) => {
      if (isValid === !prev.has(name)) {
        return prev;
      }
      const next = new Set(prev);
      if (isValid) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);
  const handleSelectChange = (name, o) => {
    const newOptions = o.map((option) => option.value);
    setProject({
      ...project,
      [name]: newOptions,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updateProject = { ...project };
    if (updateProject.policy_type_crm_id === '') {
      updateProject.policy_type_crm_id = null;
      updateProject.policy_type = null;
    }

    ProjectService.updateProject(updateProject).then((res) => {
      toggleEdit(false);
      const updatedPolicy = updateProject.policy_type_crm_id
        ? policies.find((p) => p.crm_id === updateProject.policy_type_crm_id)
        : null;
      setProject({ ...updateProject, policy_type: updatedPolicy?.policy_title ?? '' });
    });
  };

  const handleToggle = () => {
    toggleEdit(true);
  };


  if (loading) {
    return <LoadingSpinner />;
  }

  const defaultPolicyType =
    policies.length > 0
      ? policies.find((p) => p.policy_title === project.policy_type)
      : '';
  let policyMap =
    policies.length > 0 ? (
      policies?.map((p) => {
        return (
          <option key={p.crm_id} value={p.crm_id}>
            {p.policy_title}
          </option>
        );
      })
    ) : (
      <option value={project.policy_type}>{project.policy_type}</option>
    );

  return (
    <form className='detailCard-project' onSubmit={(e) => handleSubmit(e)}>
      <div className='detailProjectHeader alignCenter width-100'>
        <div className='width-20 flex-space'>
          <h3 className='bolder'>{project.project_number}</h3>
          <p>-</p>
          <h3 className='bolder'>
            {editable === true ? (
              <input
                name='project_name'
                onChange={(e) => handleInputChange(e)}
                className='edit-input bolder'
                value={project.project_name}
              />
            ) : (
              project.project_name
            )}
          </h3>
        </div>
        <div className='flex-end width-70'>
          {editable === false ? (
            <button
              type='button'
              onClick={() => handleToggle()}
              className='cta-button2 remove-text-decoration'
            >
              EDIT PROJECT
            </button>
          ) : (
            <input
              type='submit'
              className='cta-button remove-text-decoration'
              value='SAVE'
              disabled={invalidFields.size > 0}
            />
          )}
        </div>
      </div>
      <Tabs>
        <div label={<span>Phius Project Data <strong>Read Only</strong></span>}>
          <PhiusProjectDataTab project={project} />
        </div>
        <div label='Project Overview'>
          <ProjectOverviewTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
            policyMap={policyMap}
            defaultPolicyType={defaultPolicyType}
          />
        </div>
        <div label='Project Information'>
          <ProjectInformationTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
          />
        </div>
        <div label='Wall, Roof, Floor Types'>
          <WallFloorRoofTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
          />
        </div>
        <div label='Window & Door Types'>
          <WindowDoorTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
          />
        </div>
        <div label='Ventilation, Heating & Cooling Equipment'>
          <VentilationHeatingCoolingTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
          />
        </div>
        <div label='Other Building Equipment, Renewable Energy'>
          <OtherEquipmentTab
            project={project}
            editable={editable}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            handleFieldValidityChange={handleFieldValidityChange}
          />
        </div>
        <div label='Photos'>
          <PhotosTab />
        </div>
      </Tabs>
    </form>
  );
}
